// services/captcha/types.ts
//
// The gateway's captcha contract (captcha-client-integration.md). Provider-
// neutral: nothing here knows about ARCaptcha or any other widget.

/** `data.captcha` of a 428 CAPTCHA_REQUIRED or 400 CAPTCHA_INVALID body. */
export type CaptchaChallenge = {
  /** Provider id; picks the adapter. Not a closed set: new ids can appear. */
  provider: string;
  /** The provider's public key. Always from the response, never hard-coded. */
  site_key: string;
  /** Provider-specific widget hints; unknown keys are ignored. */
  options?: Record<string, string>;
};

export type CaptchaErrorCode =
  | 'CAPTCHA_REQUIRED'
  | 'CAPTCHA_INVALID'
  | 'CAPTCHA_UNAVAILABLE';

/**
 * One provider's widget. A fresh adapter is made for every solve, so it can
 * keep the rendered widget's id on itself.
 */
export interface CaptchaAdapter {
  /**
   * Load the provider script (a fixed URL inside the adapter), render the
   * widget into `container` and resolve with a token exactly once; reject with
   * a CaptchaError on a widget error.
   */
  solve(
    container: HTMLElement,
    siteKey: string,
    options: Record<string, string>
  ): Promise<string>;
  /** Forget the rendered widget and its token. */
  reset(): void;
}

export type CaptchaFailure =
  /** The user closed the challenge. */
  | 'cancelled'
  /** No adapter for the provider the gateway named. */
  | 'unsupported'
  /** The provider script failed to load or the widget reported an error. */
  | 'widget'
  /** No captcha host is mounted to show the widget. */
  | 'no-host';

export class CaptchaError extends Error {
  readonly reason: CaptchaFailure;

  constructor(reason: CaptchaFailure, detail?: string) {
    super(detail ? `captcha ${reason}: ${detail}` : `captcha ${reason}`);
    this.reason = reason;
  }
}
